import { useState, useCallback, useRef } from 'react'
import { warn, isObject, isFunction } from '../../core'

type UseStatesReturn<S extends Object> = [S, UseStatesDispatch<S>, () => S]

type UseStatesStore<S> = {
  /** 存储最新的值 */
  newData: S,
  /** 脏数据 */
  dirtyData?: S
}

const { assign } = Object

export function useStates<S extends Object>(defaultValue: S | (() => S)): UseStatesReturn<S> {

  // 派生状态
  const [ data, setData ] = useState<S>(() => {
    let _state: any = defaultValue
    if (isFunction(defaultValue)) _state = (defaultValue as Function)()
    if (!isObject(_state)) {
      warn('useStates的默认参数必须是一个对象')
      _state = {}
    }
    return _state
  })
  // 存储当前hooks的一些状态
  const store = useRef<UseStatesStore<S>>({ newData: data, dirtyData: undefined })

  return [
    data,
    useCallback((
      function (valueOrUpdateKey: any, newValueOrGetNewValue: any) {
        const storeValue = store.current
        const { newData, dirtyData } = storeValue
        const oldValue = dirtyData || newData
        // 验证是否需要更新
        let needUpdate: boolean
        // 判断是否是单节点更新
        if (typeof valueOrUpdateKey === 'string') {
          const newValue = isFunction(newValueOrGetNewValue) ? newValueOrGetNewValue(oldValue[valueOrUpdateKey]) : newValueOrGetNewValue
          if (needUpdate = oldValue[valueOrUpdateKey] !== newValue) {
            // 先放到脏数据中
            storeValue.dirtyData = assign(oldValue, { [valueOrUpdateKey]: newValue })
          }
        }
        // 批量更新
        else {
          const nextState: Partial<S> = isFunction(valueOrUpdateKey) ? valueOrUpdateKey(oldValue) : valueOrUpdateKey
          // 判断是否需要更新
          if (needUpdate = Object.keys(nextState).some(k => nextState[k] !== oldValue[k])) {
            storeValue.dirtyData = assign(oldValue, nextState)
          }
        }
        // 如果脏数据中没值，那么放到微任务队列中，等待宏任务执行完毕出发更新
        if (needUpdate && !dirtyData) {
          queueMicrotask(() => {
            const dirtyData = storeValue.newData = { ...storeValue.dirtyData! }
            // 清空脏数据
            storeValue.dirtyData = undefined
            setData(dirtyData)
          })
        }
      }
    ), []) as UseStatesReturn<S>[1],
    useCallback(() => store.current.newData, [])
  ]
}
